/**
 * Recent activity component for dashboard
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../utils/formatters';
import './RecentActivity.css';

/**
 * List of recently added or emptied bottles with toggle
 * @param {Object} props - Component props
 * @param {Array} props.recentBottles - Recently added bottles
 * @param {boolean} props.recentLoading - Loading state for recent
 * @param {Array} props.emptiedBottles - Recently emptied bottles
 * @param {boolean} props.emptiedLoading - Loading state for emptied
 * @returns {React.ReactElement} Activity element
 */
export default function RecentActivity({
  recentBottles = [],
  recentLoading,
  emptiedBottles = [],
  emptiedLoading,
  orderedBottles = [],
  orderedLoading
}) {
  const [mode, setMode] = useState('additions');

  const bottles = mode === 'additions' ? recentBottles : mode === 'emptied' ? emptiedBottles : orderedBottles;
  const loading = mode === 'additions' ? recentLoading : mode === 'emptied' ? emptiedLoading : orderedLoading;
  const emptyMessage = mode === 'additions' ? 'No bottles added yet' : mode === 'emptied' ? 'No empty bottles yet' : 'No ordered bottles';
  const title = mode === 'additions' ? 'Recent Additions' : mode === 'emptied' ? 'Recently Emptied' : 'Ordered';
  const dateLabel = mode === 'emptied'
    ? b => b.updated_at ? formatDate(b.updated_at) : 'No date'
    : b => b.purchase_date ? formatDate(b.purchase_date) : 'No date';

  return (
    <div className="recent-activity">
      <div className="recent-activity__header">
        <h3 className="recent-activity__title">{title}</h3>
        <div className="recent-activity__toggle">
          <button
            className={`recent-activity__toggle-btn${mode === 'additions' ? ' recent-activity__toggle-btn--active' : ''}`}
            onClick={() => setMode('additions')}
          >
            Additions
          </button>
          <button
            className={`recent-activity__toggle-btn${mode === 'ordered' ? ' recent-activity__toggle-btn--active' : ''}`}
            onClick={() => setMode('ordered')}
          >
            Ordered
          </button>
          <button
            className={`recent-activity__toggle-btn${mode === 'emptied' ? ' recent-activity__toggle-btn--active' : ''}`}
            onClick={() => setMode('emptied')}
          >
            Emptied
          </button>
        </div>
      </div>

      {loading ? (
        <div className="recent-activity__loading">Loading...</div>
      ) : bottles.length === 0 ? (
        <div className="recent-activity__empty">{emptyMessage}</div>
      ) : (
        <ul className="recent-activity__list">
          {bottles.map(bottle => (
            <li key={bottle.id} className="recent-activity__item">
              <Link to={`/bottles/${bottle.id}`} className="recent-activity__link">
                <div className="recent-activity__info">
                  <span className="recent-activity__name">{bottle.bottle_name}</span>
                  <span className="recent-activity__type">
                    {bottle.type}
                    {bottle.distilleries?.distillery_name && ` - ${bottle.distilleries.distillery_name}`}
                  </span>
                </div>
                <div className="recent-activity__meta">
                  <span className={`recent-activity__status recent-activity__status--${bottle.status.toLowerCase()}`}>
                    {bottle.status}
                  </span>
                  <span className="recent-activity__date">
                    {dateLabel(bottle)}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <Link to="/bottles" className="recent-activity__view-all">
        View all bottles &rarr;
      </Link>
    </div>
  );
}
